/**
 * Safe, eval-free mathematical expression evaluator for the Exam Calculator.
 * Supports standard arithmetic (+, -, *, /), exponents (^), parentheses (),
 * unary minus, and floating point precision handling.
 */

// Tokenize mathematical string expression
function tokenize(rawExpr) {
  // Normalize visual operators to standard ones
  const expr = rawExpr
    .replace(/×/g, '*')
    .replace(/÷/g, '/')
    .replace(/−/g, '-')
    .trim();

  const tokens = [];
  let i = 0;

  while (i < expr.length) {
    const ch = expr[i];

    if (/\s/.test(ch)) {
      i++;
      continue;
    }

    if (/\d/.test(ch) || ch === '.') {
      let numStr = ch;
      i++;
      let hasDot = ch === '.';
      while (i < expr.length && (/[\d.]/.test(expr[i]))) {
        if (expr[i] === '.') {
          if (hasDot) break;
          hasDot = true;
        }
        numStr += expr[i];
        i++;
      }
      tokens.push({ type: 'number', value: parseFloat(numStr) });
      continue;
    }

    if (['+', '-', '*', '/', '^', '(', ')'].includes(ch)) {
      tokens.push({ type: 'op', value: ch });
      i++;
      continue;
    }

    // Ignore unsupported characters safely
    i++;
  }

  // Insert implicit multiplication: e.g. 5(3) => 5 * (3) or (2)(3) => (2) * (3)
  const normalized = [];
  for (let j = 0; j < tokens.length; j++) {
    const curr = tokens[j];
    const prev = tokens[j - 1];

    if (
      prev &&
      (prev.type === 'number' || prev.value === ')') &&
      (curr.value === '(' || curr.type === 'number')
    ) {
      normalized.push({ type: 'op', value: '*' });
    }
    normalized.push(curr);
  }

  return normalized;
}

// Recursive Descent Parser
class Parser {
  constructor(tokens) {
    this.tokens = tokens;
    this.pos = 0;
  }

  peek() {
    return this.tokens[this.pos] || null;
  }

  consume() {
    return this.tokens[this.pos++] || null;
  }

  parse() {
    if (this.tokens.length === 0) return 0;
    const result = this.parseExpression();
    if (this.pos < this.tokens.length) {
      throw new Error('Invalid syntax');
    }
    return result;
  }

  parseExpression() {
    let result = this.parseTerm();
    while (this.peek() && (this.peek().value === '+' || this.peek().value === '-')) {
      const op = this.consume().value;
      const right = this.parseTerm();
      if (op === '+') result += right;
      else result -= right;
    }
    return result;
  }

  parseTerm() {
    let result = this.parseFactor();
    while (this.peek() && (this.peek().value === '*' || this.peek().value === '/')) {
      const op = this.consume().value;
      const right = this.parseFactor();
      if (op === '*') {
        result *= right;
      } else {
        if (right === 0) throw new Error('Cannot divide by zero');
        result /= right;
      }
    }
    return result;
  }

  parseFactor() {
    const base = this.parseUnary();
    if (this.peek() && this.peek().value === '^') {
      this.consume();
      const exponent = this.parseFactor(); // Right-associative
      return Math.pow(base, exponent);
    }
    return base;
  }

  parseUnary() {
    const next = this.peek();
    if (next && next.value === '-') {
      this.consume();
      return -this.parseUnary();
    }
    if (next && next.value === '+') {
      this.consume();
      return this.parseUnary();
    }
    return this.parsePrimary();
  }

  parsePrimary() {
    const token = this.consume();
    if (!token) throw new Error('Unexpected end of expression');

    if (token.type === 'number') {
      return token.value;
    }

    if (token.value === '(') {
      const result = this.parseExpression();
      const closing = this.consume();
      if (!closing || closing.value !== ')') {
        throw new Error('Missing closing parenthesis');
      }
      return result;
    }

    throw new Error('Invalid syntax');
  }
}

export function evaluateExpression(exprStr) {
  if (!exprStr || !exprStr.trim()) return '';
  try {
    const tokens = tokenize(exprStr);
    if (tokens.length === 0) return '';
    const parser = new Parser(tokens);
    const result = parser.parse();
    return formatCalculatorResult(result);
  } catch (err) {
    return err.message === 'Cannot divide by zero' ? 'Cannot divide by zero' : 'Error';
  }
}

export function formatCalculatorResult(num) {
  if (typeof num !== 'number' || isNaN(num)) return 'Error';
  if (!isFinite(num)) return 'Cannot divide by zero';

  // Fix binary floating point precision artifacts (e.g. 0.1 + 0.2 = 0.3)
  const precisionFixed = parseFloat(num.toPrecision(12));

  // Use scientific notation for very large or tiny magnitudes
  if (Math.abs(precisionFixed) >= 1e12 || (Math.abs(precisionFixed) < 1e-7 && precisionFixed !== 0)) {
    return precisionFixed.toExponential(5).replace('+', '');
  }

  // Format with sensible decimal length
  return precisionFixed.toString();
}
